import React from 'react';
import { FaGithub, FaExternalLinkAlt, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import '../assets/components/_projects.scss';

// Import DCInject images
import dcinjectBlockDiagram from '../assets/images/DCIject/DCInject-block-diagram.png';
import attackResult from '../assets/images/DCIject/attack-result.png';
import defenseResult from '../assets/images/DCIject/Defense-result.png';


const Projects = () => {
  const projects = {
    publications: [
      {
        title: "DCInject: Persistent Backdoor Attacks via Frequency Manipulation in Personalized Federated Learning",
        description: (
          <>
            Adaptive frequency-domain backdoor attack for Personalized Federated Learning (PFL) that achieves superior
            attack success rates while maintaining clean accuracy, evaluated under parameter decoupling-based
            personalization.
            {' '}
            <a
              href="https://arxiv.org/html/2602.18489v1"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#3182ce', fontWeight: 600 }}
            >
              arXiv
            </a>
            {' · '}
            <a
              href="https://github.com/NahomMA/DCINject-PFL"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#3182ce', fontWeight: 600 }}
            >
              GitHub
            </a>
          </>
        ),
        badge: "Published: Oral Presentation",
        badgeType: "accepted",
        comingSoon: true,
        statusText: "ICASSP 2026",
        images: [
          { src: dcinjectBlockDiagram, alt: "DCInject Block Diagram", label: "Block Diagram" },
          { src: attackResult, alt: "Attack Result", label: "Attack Results" },
          { src: defenseResult, alt: "Defense Result", label: "Defense Results" }
        ]
      }
    ],
    // Described generally while double-blind peer review is in progress — no titles,
    // system names, or metrics that could identify the submissions.
    underReview: [
      {
        title: "Measuring LLM Susceptibility to Tool-Use Manipulation",
        description: "A controlled evaluation of how adversarial content reaching a language model through its tooling can redirect the model's behavior. The framework isolates the model's own susceptibility from properties of the surrounding system, and quantifies how much a layered defense recovers.",
        badge: "Under Review · 2026",
        badgeType: "ongoing",
        comingSoon: true,
        statusText: "Details after peer review concludes"
      },
      {
        title: "Defending LLM Agents at Execution Time",
        description: "Autonomous agents act through external tools, and content those tools return can steer what the agent does next. This work screens each action an agent proposes before it executes, using signals from the agent's own history rather than trusting attacker-influenceable inputs, with explicit and tunable control over how often benign behavior is flagged.",
        badge: "Ongoing Research",
        badgeType: "ongoing",
        comingSoon: true,
        statusText: "In preparation"
      }
    ],
    openResearch: [
      {
        title: "Tokens to Agents — Building the LLM Stack from First Principles",
        description: (
          <>
            A seven-part open-source series and writing project rebuilding the path from classical language models to secure AI agents:
            n-grams, embeddings, neural language modeling, transformers, LLMs and prompting, agentic AI, and
            agentic AI security. Each part ships reproducible code, a technical article, and an interactive demo.
            {' '}
            <a href="https://github.com/NahomMA/tokens-to-agents" target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', fontWeight: 600 }}>GitHub</a>
            {' · '}
            <a href="https://medium.com/@nahombirhan/the-textbook-fix-that-made-my-language-model-5-worse-e8586d5639b5" target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', fontWeight: 600 }}>Part 1 on Medium</a>
            {' · '}
            <a href="https://huggingface.co/spaces/Nahom-M/tokens-to-agents-01-ngrams" target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', fontWeight: 600 }}>Interactive demo</a>
          </>
        ),
        badge: "Open-Source Project · Part 1 live",
        badgeType: "ongoing",
        techStack: "Python, uv, Streamlit, Plotly"
      }
    ],
    research: [
      {
        title: "EdgeML and Cybersecurity: Using Machine Learning Algorithms to detect impostor edge devices",
        description: "This research focuses on security, specifically impersonation in a sensor network. This research is to build a light impostor detector ML model for edge devices. The model is also expected to detect sequence transitions if the impostor starts transmitting after its data is transmitted.",
        badge: "Research",
        badgeType: "research",
        link: "https://drive.google.com/file/d/1Zh-IXxYftJmgB37RXKo7kX4sslCkgrov/view?usp=sharing",
        linkText: "View on Google Drive",
        techStack: "Raspberry PI, TensorFlow Lite, Python"
      }
    ],
    course: [
      {
        title: "Blockchain-Based Healthcare Data Security",
        description: "Proposed system integrating blockchain and IPFS for secure medical records management.",
        badge: "Course",
        badgeType: "course",
        link: "https://drive.google.com/file/d/1h43EViKmhozlZESDcwPkf-vIL4SdNVfo/view?usp=sharing",
        linkText: "View on Google Drive",
        techStack: "IPFS, AWS, Blockchain"
      },
      {
        title: "Malware Detection",
        description: "Machine learning-based malware detection system with comprehensive analysis tools.",
        badge: "Course",
        badgeType: "course",
        link: "https://github.com/NahomMA/malware_detection",
        linkText: "View on GitHub",
        icon: <FaGithub />
      }
    ],
    industry: [
      {
        title: "CS Investigations Camera Solutions Platform",
        description: "Developed comprehensive camera solutions platform for investigative purposes.",
        badge: "Industry",
        badgeType: "industry",
        team: true,
        techStack: "Full-stack development, Camera APIs"
      },
      {
        title: "Dumpster Fullness Detection",
        description: "Computer vision system for monitoring dumpster capacity and optimizing waste collection.",
        badge: "Industry",
        badgeType: "industry",
        techStack: "Computer Vision, IoT, ML"
      },
      {
        title: "TinyML Tool Assessment and Prototyping",
        description: "Evaluated and prototyped TinyML tools for edge device machine learning applications.",
        badge: "Industry",
        badgeType: "industry",
        techStack: "TinyML, Edge Computing, ML"
      }
    ],
    fun: [
      {
        title: "Prime and Composite Classification",
        description: "Created dataset of 2 million samples 1 million sample for each class, developed models using sequence models and 1D-CNN.",
        badge: "Fun",
        badgeType: "fun",
        link: "https://github.com/NahomMA/Prime_and_composite_num_lassification",
        linkText: "View on GitHub",
        icon: <FaGithub />,
        techStack: "Python, TensorFlow, NumPy"
      }
    ]
  };



  const renderProjectCard = (project, index) => (
    <div key={index} className="clean-card project-card">
      <div className="card-header">
        <div className="project-info">
          <h3>{project.title}</h3>
          <div className="meta-info">
            <span className="badge">{project.badge}</span>
            {project.team && <span className="team-badge">Team</span>}
          </div>
        </div>
      </div>

      <p>{project.description}</p>

      {project.images && (
        <div className="project-images">
          {project.images.map((img, idx) => (
            <div key={idx} className="project-image-container">
              <img src={img.src} alt={img.alt} className="project-image" />
              <span className="image-label">{img.label}</span>
            </div>
          ))}
        </div>
      )}

      {project.techStack && (
        <div className="tech-stack">
          {project.techStack.split(', ').map((tech, idx) => (
            <span key={idx}>{tech}</span>
          ))}
        </div>
      )}

      {project.comingSoon ? (
        <div className="coming-soon">
          <span>{project.statusText || "Details coming soon"}</span>
        </div>
      ) : project.link ? (
        <div className="project-link">
          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
          >
            {project.linkText}
            {project.icon || <FaExternalLinkAlt />}
          </a>
        </div>
      ) : null}
    </div>
  );



  return (
    <div className="page-container">
      <div className="content-wrapper">
        <div className="section-header">
          <h1>Research & Projects</h1>
          <p>Research, course projects, industry experience, and personal experiments</p>
          <div className="about-actions">
              <Link to="/cv" className="btn btn-primary">
                View Full CV
                <FaArrowRight className="btn-icon" />
              </Link>
            </div>
        </div>

        {/* Publications */}
        <section className="projects-section">
          <h2>Publications</h2>
          <div className="grid-container">
            {projects.publications.map((project, index) => renderProjectCard(project, index))}
          </div>
        </section>

        {/* Under Review & Ongoing */}
        <section className="projects-section">
          <h2>Under Review & Ongoing Research</h2>
          <div className="grid-container">
            {projects.underReview.map((project, index) => renderProjectCard(project, index))}
          </div>
        </section>

        {/* Masters Research */}
        <section className="projects-section">
          <h2>Masters Research Project</h2>
          <div className="grid-container">
            {projects.research.map((project, index) => renderProjectCard(project, index))}
          </div>
        </section>

        {/* Open-Source Projects & Writing */}
        <section className="projects-section">
          <h2>Open-Source Projects & Writing</h2>
          <div className="grid-container">
            {projects.openResearch.map((project, index) => renderProjectCard(project, index))}
          </div>
        </section>

        {/* Course Projects */}
        <section className="projects-section">
          <h2>Course Projects</h2>
          <div className="grid-container">
            {projects.course.map((project, index) => renderProjectCard(project, index))}
          </div>
        </section>

        {/* Industry Projects */}
        <section className="projects-section">
          <h2>Industry Projects</h2>
          <div className="grid-container">
            {projects.industry.map((project, index) => renderProjectCard(project, index))}
          </div>
        </section>

        {/* Fun Projects */}
        <section className="projects-section">
          <h2>Fun Projects</h2>
          <div className="grid-container">
            {projects.fun.map((project, index) => renderProjectCard(project, index))}
          </div>
        </section>
        <div className="section-footer">
            <Link to="/cv" className="btn btn-outline">
              Check out my CV
              <FaArrowRight className="btn-icon" />
            </Link>
          </div>
      </div>
    </div>
  );
};

export default Projects;
